import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Stack, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material'

import InfoBlock from '../components/InfoBlock'
import UnderwritersAndGuarantorsTable from '../components/UnderwritersAndGuarantorsTable'
import { calculateIssueTotalOfferingSize, formatFeeLevel } from './utils'

import { getLevelName } from './utils'
import { EnrichedIssueEntity, IssueEntity } from '../../types/IssueEntity'
import { isUnit } from '../../utils/issues/sharesOrUnits'
import { CommitChart } from '../../components/entities/CommitChart'

const Wrapper = ({ children }: { children: React.ReactNode }) => (
    <InfoBlock title='Tecknings- och garantiåtagare' loadingElement={false}>
        {children}
    </InfoBlock>
)

function UnderwritersAndGuarantors({ issue }: { issue: EnrichedIssueEntity }) {
    const { i18n } = useTranslation()
    const locale = i18n.language

    const investors = issue.investors.filter((investor) => !investor.isSeller)

    const fees = (
        issue.fees.filter((fee) => fee.feeLevel !== null && fee.feeLevel > 0) as Array<
            IssueEntity['fees'][number] & { feeLevel: number }
        >
    ).sort((a, b) => a.feeLevel - b.feeLevel)
    const maxFeeLevel = Math.max(...issue.fees.map((level) => level.feeLevel))

    const hasCommitmentApprox =
        issue.approxCommitment > 0 || issue.approxGuarantor > 0 || issue.approxTotalCommitment > 0

    const hasNoCommitment = investors?.length === 0 && !hasCommitmentApprox

    const NoInvestorsAdditionalInformation = () => {
        return (
            <Stack spacing={2}>
                <Typography>Information om vilka som lämnat åtaganden har inte kommunicerats ännu</Typography>
                {fees.length > 0 ? (
                    <Box>
                        <Typography paddingBottom={1} fontWeight={800}>
                            Garantiersättningar:
                        </Typography>
                        <Table size='small'>
                            <TableBody>
                                {fees.map((fee) => {
                                    const feeLevelName = getLevelName(fee.feeLevel, maxFeeLevel)
                                    const formatedFeeLevel = formatFeeLevel(
                                        { fees: issue.fees, isUnit: isUnit(issue) },
                                        fee.feeLevel - 1,
                                        maxFeeLevel,
                                        locale,
                                    )
                                    return (
                                        <TableRow key={fee.feeLevel}>
                                            <TableCell width={110} padding='none' sx={{ borderBottom: 'none' }}>
                                                {feeLevelName}
                                            </TableCell>
                                            <TableCell sx={{ padding: '2px 12px', borderBottom: 'none' }}>
                                                {formatedFeeLevel}
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    </Box>
                ) : null}
            </Stack>
        )
    }

    if (hasNoCommitment) {
        return (
            <Wrapper>
                <span>Teckningsåtagande och garantiåtagande saknas</span>
            </Wrapper>
        )
    }

    const hasInvestors = investors.length > 0

    return (
        <Wrapper>
            <CommitChart issue={issue} isLeftAlign size='large' skipAnimation />
            {!hasInvestors && !issue.noInvestorsNamed ? <NoInvestorsAdditionalInformation /> : null}
            {hasInvestors && !issue.noInvestorsNamedChecked && (
                <UnderwritersAndGuarantorsTable
                    currency={issue.currency}
                    fees={issue.fees}
                    noGuarantors={!!issue.noGuarantors}
                    investors={issue.investors}
                    isUnit={isUnit(issue)}
                    totalOfferingSize={calculateIssueTotalOfferingSize(issue).totalOfferingSize}
                />
            )}
        </Wrapper>
    )
}

export default UnderwritersAndGuarantors
